/**
 * C-13 招待コード（FR-05/16・D-18）。
 *
 * ★ **発行は得意先単位**（03 §3）＝この接続がどの得意先に帰属するかを先に決めるから、
 *   接続した職人の掛率・単価・現場候補がその場で解決できる。
 * ★ **コードの失効と接続の失効は独立**（03 §3）＝コードを止めても既存の職人は使い続けられる。
 *   画面にもそう書く（書かないと「止めたのに注文が来る」と問い合わせになる）。
 */

import { Badge, Card, ConfirmDialog, Empty, ErrorNote, formatDateTime, PageHeader, Spinner, Table, Td } from "@kenzai/ui";
import { console_, type InviteCode } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { InviteShare, InviteShareDialog } from "./InviteShare";

/**
 * C-13 招待コード。**D-79 で取引先（C-09）配下のタブになった**＝サイドバーの独立項目をやめた。
 *
 * `customerId` を渡すと**その取引先だけ**の埋め込み表示になる（見出しは親が出す）。
 * 渡さない場合は横断一覧（取引先一覧のサブタブ）＝どのコードが生きているかを一覧できる。
 */
export function InviteCodes({ customerId: fixedCustomerId }: { customerId?: number } = {}) {
  const [params] = useSearchParams();
  const embedded = fixedCustomerId != null;
  const customerId = embedded ? String(fixedCustomerId) : params.get("customer_id");
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [revoking, setRevoking] = useState<InviteCode | null>(null);
  const [issuedCode, setIssuedCode] = useState<string | null>(null);
  const [sharing, setSharing] = useState<string | null>(null);

  const customers = useQuery({ queryKey: ["customers", ""], queryFn: () => console_.customers("?limit=100") });
  const codes = useQuery({ queryKey: ["invite-codes"], queryFn: () => console_.inviteCodes() });

  const issue = useMutation({
    mutationFn: (id: number) => console_.issueInviteCode(id),
    onSuccess: (c) => {
      setError(null);
      setIssuedCode(c.code);
      qc.invalidateQueries({ queryKey: ["invite-codes"] });
    },
    onError: setError,
  });

  const revoke = useMutation({
    mutationFn: (id: number) => console_.revokeInviteCode(id),
    onSuccess: () => {
      setRevoking(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["invite-codes"] });
    },
    onError: (e) => {
      setRevoking(null);
      setError(e);
    },
  });

  const byCustomer = new Map((customers.data?.items ?? []).map((c) => [c.id, c.name]));
  const rows = (codes.data ?? []).filter((c) => !customerId || c.customer_id === Number(customerId));

  return (
    <>
      {!embedded && (
        <PageHeader
          title="招待コード"
          subtitle="取引先に渡すコード。入力するとアプリから注文できるようになります。"
        />
      )}
      <ErrorNote error={error} />

      <Card style={{ marginBottom: "var(--kz-space-4)" }}>
        <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)" }}>
          新しいコードを発行する
        </h2>
        <div style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "center" }}>
          <select className="kz-select"
            id="issue-customer"
            defaultValue={customerId ?? ""}
            data-testid="issue-customer"
            style={{
              flex: 1,
              font: "inherit",
              padding: "var(--kz-space-2)",
              border: "1px solid var(--kz-color-border)",
              borderRadius: "var(--kz-radius-sm)",
            }}
          >
            <option value="">取引先を選ぶ</option>
            {(customers.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_no} {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="issue-code"
            disabled={issue.isPending}
            onClick={() => {
              const el = document.getElementById("issue-customer") as HTMLSelectElement;
              if (el.value) issue.mutate(Number(el.value));
            }}
            className="kz-btn kz-btn--primary"
          >
            発行する
          </button>
        </div>

        {issuedCode && (
          <div
            role="status"
            data-testid="issued-code"
            style={{
              marginTop: "var(--kz-space-4)",
              padding: "var(--kz-space-4)",
              background: "var(--kz-color-primary-weak)",
              borderRadius: "var(--kz-radius-sm)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
              このコードを取引先にお伝えください
            </div>
            <div
              className="kz-num"
              style={{ fontSize: 32, fontWeight: 700, letterSpacing: "0.2em", color: "var(--kz-color-primary)" }}
            >
              {issuedCode}
            </div>
            <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
              {/* 03 §3 の既定＝90日・20人まで。**紙・口頭で渡せる**ことが要件（06 §2.2） */}
              有効期限 90日・20人まで利用できます。
            </div>
            {/* ★リンク・QR は `issued-code` の**外**に出す（この要素は E2E がコードの
                8桁を厳密に見ているので、中に URL を足すと文字列比較が壊れる） */}
          </div>
        )}
        {issuedCode && (
          <div style={{ marginTop: "var(--kz-space-4)" }}>
            <InviteShare code={issuedCode} />
          </div>
        )}
      </Card>

      {codes.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty title="発行済みのコードはありません" hint="上のフォームから発行してください。" />
      ) : (
        <Card padding="0">
          <Table head={["コード", "取引先", "利用状況", "有効期限", "発行日", ""]}>
            {rows.map((c) => {
              const expired = c.expires_at !== null && new Date(c.expires_at) <= new Date();
              const full = c.used_count >= c.max_uses;
              return (
                <tr key={c.id} data-testid={`code-${c.id}`}>
                  <Td>
                    <span
                      className="kz-num"
                      style={{
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textDecoration: c.revoked_at ? "line-through" : undefined,
                        color: c.revoked_at ? "var(--kz-color-ink-muted)" : undefined,
                      }}
                    >
                      {c.code}
                    </span>
                  </Td>
                  <Td>{c.customer_id ? (byCustomer.get(c.customer_id) ?? `#${c.customer_id}`) : "—"}</Td>
                  <Td>
                    <span className="kz-num">
                      {c.used_count} / {c.max_uses}
                    </span>{" "}
                    {c.revoked_at ? (
                      <Badge tone="danger">失効</Badge>
                    ) : expired ? (
                      <Badge tone="warn">期限切れ</Badge>
                    ) : full ? (
                      <Badge tone="warn">上限</Badge>
                    ) : (
                      <Badge tone="ok">有効</Badge>
                    )}
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    <span className="kz-num">{c.expires_at ? formatDateTime(c.expires_at) : "—"}</span>
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    <span className="kz-num">{formatDateTime(c.created_at)}</span>
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    {/* ★発行直後にしかリンク/QR が出ないと「あとでもう一度配りたい」で詰む。
                        まだ使える行（未失効・期限内・上限未満）からいつでも出せるようにする。 */}
                    {!c.revoked_at && !expired && !full && (
                      <button
                        type="button"
                        onClick={() => setSharing(c.code)}
                        data-testid={`share-${c.id}`}
                        className="kz-btn kz-btn--sm"
                        style={{ marginRight: "var(--kz-space-2)" }}
                      >
                        リンク・QR
                      </button>
                    )}
                    {!c.revoked_at && (
                      <button
                        type="button"
                        onClick={() => setRevoking(c)}
                        data-testid={`revoke-${c.id}`}
                        className="kz-btn kz-btn--sm"
                      >
                        失効させる
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        </Card>
      )}

      {sharing && <InviteShareDialog code={sharing} onClose={() => setSharing(null)} />}

      {revoking && (
        <ConfirmDialog
          title="このコードを失効させますか？"
          body={
            <>
              以後このコードでは接続できなくなります。
              <br />
              {/* 03 §3：**独立**＝ここを書かないと「止めたのに注文が来る」と問い合わせになる */}
              <strong>すでに接続している取引先はそのままご利用いただけます。</strong>
              {/* 行き先（C-20）はページ上部のボタン＝**ダイアログの中にリンクを置かない**
                  （押した瞬間にこの失効操作が黙って捨てられる） */}
              取引を終える場合は、このあと画面右上の「接続（アプリ利用者）」から解除してください。
            </>
          }
          confirmLabel="失効させる"
          tone="danger"
          busy={revoke.isPending}
          onConfirm={() => revoke.mutate(revoking.id)}
          onCancel={() => setRevoking(null)}
        />
      )}
    </>
  );
}
